import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef } from "react";
import { useUniqueId } from "../../hooks/useUniqueId";
import { FieldLabel } from "../field-label/FieldLabel";
import { FieldContent } from "../field-content/index";
import { FieldAdornmentWrapper } from "../field-adornment-wrapper/FieldAdornmentWrapper";
import { FieldStatus } from "../field-status/FieldStatus";
import { FieldDescription } from "../field-description/FieldDescription";
import { RadioGroup } from "../radio/radio-group/RadioGroup";
import { CheckboxGroup } from "../checkbox/checkbox-group/CheckboxGroup";
import { Fieldset } from "../fieldset/Fieldset";
import * as Styled from "./Field.styled";
export const Field = forwardRef((props, ref) => {
    // @ts-expect-error displayName exists
    const inputDisplayName = props.input?.type.displayName || "";
    /* istanbul ignore next */
    const { children, description, disabled, endAdornment, endAdornmentWidth: endAdornmentWidthProp, helpText, gutterAdornment, id, isFullWidth, input, label, sizeVariant = "default", alignAdornments = ["TextArea", "AutosuggestMultiSelect"].includes(inputDisplayName) ? "start" : "center", endAdornmentOffset = ["NumberInput", "Select"].includes(inputDisplayName) ? 32 : undefined, labelId: labelIdProp, labelVariant, labelMaxWidth, labelWidth, shouldVisuallyHideLabel, layoutVariant = "stacked", required, palette, startAdornment, startAdornmentWidth: startAdornmentWidthProp, status: statusProp, stringMap, ...otherProps } = props;
    const endAdornmentWidth = endAdornment ? endAdornmentWidthProp || Styled.sizeMap[sizeVariant].adornmentWidth : 0;
    const startAdornmentWidth = startAdornment
        ? startAdornmentWidthProp || Styled.sizeMap[sizeVariant].adornmentWidth
        : 0;
    const uniqueId = useUniqueId("field-");
    const uniqueDescriptionId = useUniqueId("fieldDescription-");
    const inputId = id || uniqueId;
    const labelId = labelIdProp || `${inputId}-label`;
    const statusId = `${inputId}-status`;
    const inputType = input && input.type;
    const status = typeof statusProp === "string"
        ? statusProp === ""
            ? undefined
            : { message: statusProp, level: "error" }
        : statusProp;
    if (inputType === RadioGroup || inputType === CheckboxGroup) {
        return _jsx(Fieldset, { ...props });
    }
    const hasAdornment = Boolean(startAdornment || endAdornment || gutterAdornment);
    const statusPalette = (() => {
        if (palette) {
            return palette;
        }
        if (status) {
            return status.level === "warning" ? "expressiveWarning" : "expressiveNegative";
        }
        return undefined;
    })();
    // TODO: Move to context and `useField` hooks.
    const clonedInput = input
        ? React.cloneElement(input, {
            palette: statusPalette,
            sizeVariant,
            startAdornmentWidth,
            endAdornmentWidth,
            ...input.props,
            hasStatus: Boolean(status),
            // TODO: clarify if Field provided props should really take precedence in <Field id="field1" input={<input id="field2" />}/>
            id: inputId,
            fieldLabel: label,
            labelId, // for Select (TODO: This should just use aria-labelledby directly I think)
            isFullWidth,
            "aria-describedby": [description ? uniqueDescriptionId : "", status ? statusId : ""].filter(Boolean).join(" ") || undefined,
            disabled: disabled ? disabled : input.props.disabled,
            required
        })
        : null;
    return (_jsx(Styled.Wrapper, { ref: ref, layoutVariant: layoutVariant, alignAdornments: alignAdornments, isFullWidth: Boolean(isFullWidth), endAdornmentWidth: endAdornmentWidth, startAdornmentWidth: startAdornmentWidth, "data-testid": "Field", sizeVariant: sizeVariant, ...otherProps, children: input ? (_jsxs(_Fragment, { children: [label && (_jsx(FieldLabel, { htmlFor: inputId, id: labelId, maxWidth: labelMaxWidth, width: labelWidth, variant: labelVariant, required: required, disabled: disabled, layoutVariant: layoutVariant, shouldVisuallyHideLabel: shouldVisuallyHideLabel, helpText: helpText, stringMap: stringMap, children: label })), _jsxs(FieldContent, { children: [hasAdornment ? (_jsx(FieldAdornmentWrapper, { disabled: disabled, sizeVariant: sizeVariant, endAdornmentOffset: endAdornmentOffset, alignAdornments: alignAdornments, startAdornment: startAdornment, startAdornmentWidth: startAdornmentWidth, endAdornment: endAdornment, endAdornmentWidth: endAdornmentWidth, gutterAdornment: gutterAdornment, isFullWidth: isFullWidth, children: clonedInput })) : (clonedInput), description && _jsx(FieldDescription, { id: uniqueDescriptionId, children: description }), _jsx(FieldStatus, { id: statusId, ...status })] })] })) : (children) }));
});
Field.displayName = "Field";
Field.defaultProps = {
    layoutVariant: "stacked"
};
